/**
 * Extracts FAQ question/answer pairs from TipTap JSON content.
 * Looks for FAQ section patterns and extracts details/accordion blocks.
 */

interface FaqItem {
  question: string;
  answer: string;
}

interface TipTapNode {
  type: string;
  content?: TipTapNode[];
  text?: string;
  attrs?: Record<string, unknown>;
}

/**
 * Recursively extracts text content from a TipTap node
 */
function extractTextFromNode(node: TipTapNode): string {
  if (node.text) {
    return node.text;
  }
  
  if (node.content && Array.isArray(node.content)) {
    return node.content.map(extractTextFromNode).join('');
  }
  
  return '';
}

/**
 * Checks if a heading node contains FAQ-related text
 */
function isFaqHeading(node: TipTapNode): boolean {
  const text = extractTextFromNode(node).toLowerCase();
  return text.includes('faq') || text.includes('frequently asked questions');
}

/**
 * Extracts FAQ items from TipTap content JSON
 * @param content - TipTap JSON content (can be object or string)
 * @returns Array of FAQ items with question and answer
 */
export function extractFaqFromContent(content: unknown): FaqItem[] {
  if (!content) {
    return [];
  }

  // Parse content if it's a string
  let parsedContent: TipTapNode;
  try {
    parsedContent = typeof content === 'string' ? JSON.parse(content) : content as TipTapNode;
  } catch {
    return [];
  }

  if (!parsedContent.content || !Array.isArray(parsedContent.content)) {
    return [];
  }

  const faqs: FaqItem[] = [];
  let inFaqSection = false;

  for (const node of parsedContent.content) {
    // Check if we're entering an FAQ section (heading with FAQ-related text)
    if (node.type === 'heading' && isFaqHeading(node)) {
      inFaqSection = true;
      continue;
    }

    // Check for faqBlock type (custom TipTap extension)
    if (node.type === 'faqBlock' && node.attrs) {
      const items = node.attrs.faqs as Array<{ question: string; answer: string }> | undefined;
      if (items && Array.isArray(items)) {
        for (const item of items) {
          if (item.question && item.answer) {
            faqs.push({
              question: item.question.trim(),
              answer: item.answer.trim(),
            });
          }
        }
      }
      continue;
    }

    // If we're in an FAQ section, look for details blocks (accordion items)
    if (inFaqSection && node.type === 'details') {
      let question = '';
      let answer = '';

      if (node.content && Array.isArray(node.content)) {
        for (const detailChild of node.content) {
          if (detailChild.type === 'detailsSummary') {
            question = extractTextFromNode(detailChild).trim();
          } else if (detailChild.type === 'detailsContent') {
            answer = extractTextFromNode(detailChild).trim();
          }
        }
      }

      if (question && answer) {
        faqs.push({ question, answer });
      }
    }

    // If we hit another major heading after FAQ section, stop looking
    if (inFaqSection && node.type === 'heading') {
      const headingText = extractTextFromNode(node).toLowerCase();
      // If it's not an FAQ-related heading, we've left the FAQ section
      if (!headingText.includes('faq') && !headingText.includes('frequently')) {
        inFaqSection = false;
      }
    }
  }

  return faqs;
}

export type { FaqItem };
